import { useState } from 'react'
import { api, fieldErrorsFromDetail } from '../lib/api'
import { daysUntil } from '../lib/format'
import ConfirmDialog from './ConfirmDialog'
import FancyNotePicker, { useFancyNotes } from './FancyNotePicker'
import SpaceForm from './SpaceForm'

// The New space dialog, after the admin screens proposal: an ice head with
// the fancy-note step, then the three numbered sections of the space form
// (components/SpaceForm.jsx). The brief and the invitation wording are not
// here — the note fills the brief, and both are edited in Settings, which the
// head says. Nothing is emailed on create: the principal gets their personal
// link when the space is shared, participants the links you send.
//
// "Start from a fancy-note": the first client meeting usually already
// produced one, so picking it fills the brief and suggests the engagement
// title. The brief is imported by a second request right after create (POST
// …/brief/import — the same call Settings makes), so a failed import still
// leaves a created space: the dialog then says so and offers to open it. The
// step is hidden entirely when the server answers 409 — the integration is
// off there.

// Same shape check as schemas/participants.py EMAIL_RE.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

const EMPTY = {
  client_name: '',
  engagement: '',
  ends_at: '',
  template_id: '',
  purpose: '',
  principal_name: '',
  principal_title: '',
  principal_email: '',
  notify_principal: false,
}

// Discovery is the default pick, so it leads the list; the rest keep the
// API's order. Archived templates are hidden.
function orderTemplates(templates) {
  const live = templates.filter((template) => !template.archived_at)
  const discovery = live.find((template) => template.name === 'Discovery')
  return discovery ? [discovery, ...live.filter((template) => template !== discovery)] : live
}

// "<principal first name> - <Template> Agent" — mirrors
// services/spaces.default_sender_name, which the server applies on create.
function defaultSender(principalName, template) {
  const first = principalName.trim().split(/\s+/)[0] || 'Capston'
  return `${first} - ${template?.name ?? 'Capston'} Agent`
}

export default function NewSpaceDialog({ templates, onClose, onCreated }) {
  const options = orderTemplates(templates)
  const [draft, setDraft] = useState({ ...EMPTY, template_id: options[0]?.id ?? '' })
  const [fieldErrors, setFieldErrors] = useState({})
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const fancy = useFancyNotes()
  const [note, setNote] = useState(null) // the fancy-note picked, if any
  const [suggested, setSuggested] = useState('') // the title it put in Engagement
  const [created, setCreated] = useState(null) // the space, when the import after it failed

  const template = options.find((option) => option.id === draft.template_id)

  function set(field, value) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  // Suggests, never overrides: the note's title lands in Engagement only while
  // that field is empty or still holds the previous suggestion. Anything the
  // admin typed there stays theirs.
  function handlePick(picked) {
    setNote(picked)
    const untouched = draft.engagement.trim() === '' || draft.engagement === suggested
    if (picked) {
      if (untouched) set('engagement', picked.title)
      setSuggested(picked.title)
    } else {
      if (untouched) set('engagement', '')
      setSuggested('')
    }
  }

  const endDateValid = /^\d{4}-\d{2}-\d{2}$/.test(draft.ends_at) && daysUntil(draft.ends_at) >= 0
  const valid =
    draft.client_name.trim() !== '' &&
    draft.engagement.trim() !== '' &&
    endDateValid &&
    draft.template_id !== '' &&
    draft.principal_name.trim() !== '' &&
    EMAIL_RE.test(draft.principal_email.trim())

  async function handleSubmit(event) {
    event.preventDefault()
    if (!valid || submitting) return
    setSubmitting(true)
    setError(null)
    setFieldErrors({})
    try {
      const space = await api('/api/v1/spaces', {
        method: 'POST',
        body: {
          client_name: draft.client_name.trim(),
          engagement: draft.engagement.trim(),
          ends_at: draft.ends_at,
          template_id: draft.template_id,
          principal_name: draft.principal_name.trim(),
          principal_title: draft.principal_title.trim() || null,
          principal_email: draft.principal_email.trim().toLowerCase(),
          purpose: draft.purpose.trim() || null,
          notify_principal: draft.notify_principal,
        },
      })
      if (!note) {
        onCreated(space)
        return
      }
      // The space exists from here on: a failed import is reported, not undone.
      try {
        onCreated(
          await api(`/api/v1/spaces/${space.id}/brief/import`, {
            method: 'POST',
            body: { note_id: note.id },
          }),
        )
      } catch (err) {
        setCreated(space)
        setError(
          `The space was created, but the brief could not be written from “${note.title}”: ${
            err.detail || 'the note could not be read.'
          } You can try again from its Settings.`,
        )
        setSubmitting(false)
      }
    } catch (err) {
      const mapped = fieldErrorsFromDetail(err.detail)
      if (mapped) setFieldErrors(mapped)
      else setError(err.detail || 'Could not create the space.')
      setSubmitting(false)
    }
  }

  return (
    <ConfirmDialog label="New space" onClose={onClose} wide className="modal--form">
      <form onSubmit={handleSubmit} noValidate>
        <div className="dialog-head">
          <p className="overline hero__eyebrow">Spaces</p>
          <h3 className="new-space__title">New space</h3>
          <p className="dialog-head__lede">
            Everything here can be changed later from the space&rsquo;s Settings.
          </p>
          {fancy.status !== 'off' && (
            <div className="note-step">
              <FancyNotePicker
                id="new-space-note"
                label="Start from a fancy-note"
                fancy={fancy}
                value={note?.id ?? ''}
                onPick={handlePick}
                disabled={submitting || Boolean(created)}
                help={
                  note
                    ? `Claude writes the brief from “${note.title}” once the space is created.`
                    : 'Optional. Claude writes the brief from the note, and it suggests the engagement title.'
                }
              />
            </div>
          )}
        </div>

        <div className="dialog-body">
          {error && (
            <div className="alert alert--error text-body-sm" role="alert">
              {error}
            </div>
          )}

          <SpaceForm
            mode="create"
            compact
            draft={draft}
            set={set}
            fieldErrors={fieldErrors}
            templates={options}
            disabled={Boolean(created)}
            invitations={
              <div className="sender-preview">
                <p className="sender-preview__label">Invites to staff will come from</p>
                <p className="sender-preview__value">
                  {defaultSender(draft.principal_name, template)}
                </p>
                <p className="sender-preview__note">
                  Defaults from the principal’s first name and the template. Change it, and the
                  wording, in Settings.
                </p>
              </div>
            }
          />
        </div>

        <div className="dialog-foot dialog-foot--flush">
          <p className="dialog-foot__note">
            Nothing is sent to the principal when you create a space — share it when
            you&rsquo;re ready.
          </p>
          <div className="dialog-foot__actions">
            {created ? (
              // Created, brief not imported: the one honest action left is to
              // open what exists. Settings has the same picker.
              <button type="button" className="btn btn--primary" onClick={() => onCreated(created)}>
                Open space
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn--secondary btn--outlined"
                  onClick={onClose}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={!valid || submitting}>
                  {submitting ? (note ? 'Creating and writing the brief…' : 'Creating…') : 'Create space'}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </ConfirmDialog>
  )
}
